import type { NavigateFunction } from "react-router-dom";
import type { ChatEntry } from "@/types/chat";
import type { MentorNavigateState } from "@/types/mentorNavigation";
import type { ScenarioResumeNavigateState } from "@/types/scenarioNavigation";
import type { UserSession } from "@/types/session";
import { deriveWorkspacePersona, resolveChatEntryForPersona } from "@/config/workspacePersona";

/** `/` is the general hub; specialist chat entries use the full mentor page at `/mentor`. */
export function navigateToChatEntryForApp(
  navigate: NavigateFunction,
  entry: ChatEntry,
  replace = false,
  persona?: ReturnType<typeof deriveWorkspacePersona>,
) {
  const resolvedEntry = persona ? resolveChatEntryForPersona(entry, persona) : entry;

  if (resolvedEntry === "ai_mentor") {
    navigate("/", { replace, state: { entry: "ai_mentor" } satisfies MentorNavigateState });
    return;
  }

  navigate("/mentor", { replace, state: { entry: resolvedEntry } satisfies MentorNavigateState });
}

/** Re-open a saved session in the correct tool surface (Scenario Practice vs mentor chat). */
export function navigateToSessionResume(
  navigate: NavigateFunction,
  session: UserSession,
  replace = false,
) {
  if (session.chat_mode === "teacher_scenarios") {
    navigate("/tools/teacher-scenarios", {
      replace,
      state: { resumeSessionId: session.id } satisfies ScenarioResumeNavigateState,
    });
    return;
  }

  navigateToChatEntryForApp(navigate, session.chat_mode, replace);
}

/** Navigate to hub or mentor with full `location.state` (e.g. My Team → coaching seed). */
export function navigateToMentorWithState(navigate: NavigateFunction, state: MentorNavigateState, replace = false) {
  if (state.entry === "ai_mentor") {
    navigate("/", { replace, state });
    return;
  }

  navigate("/mentor", { replace, state });
}
